import { fetchQueries, fetchQueryReasons, fetchQueryAnalytics } from "@api";
import { Badge, Button, PageHeader, PageTitle, ServerTable } from "@components";
import { updateCustomer, useMerchant } from "@auth";
import NewQueryModal from "../components/NewQueryModal";
import { useTableFilters } from "@components/tables/shared";
import { useTableQuery } from "@hooks";
import { buildApiQuery, formatAmount, formatDateTime } from "@utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryReasonLabels, queryStatusColors, queryStatusLabels, queryDaysUntilDue, queryAgeInDays, isQueryOverdue, isTerminalQueryStatus } from "../common";
import QueryDetails from "../displays/QueryDetails";

const formatResolutionTime = (hours) => {
  if (hours == null) return "-";
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
};

const PAGE_SIZE = 50;

const Queries = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = useMerchant();
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [newQueryOpen, setNewQueryOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  // Reason labels are sourced from the service so they can't drift from the backend enum.
  const { data: reasonsData } = useQuery({
    queryKey: ["queries", "reasons"],
    queryFn: ({ signal }) => fetchQueryReasons({ signal }),
    staleTime: 60 * 60 * 1000,
  });
  const reasonLabels = useMemo(
    () => (reasonsData ? Object.fromEntries(reasonsData.map((r) => [r.code, r.label])) : queryReasonLabels),
    [reasonsData],
  );
  const reasonCodes = useMemo(
    () => (reasonsData ? reasonsData.map((r) => r.code) : Object.keys(queryReasonLabels)),
    [reasonsData],
  );

  const { data: analytics } = useQuery({
    queryKey: ["queries", "analytics"],
    queryFn: ({ signal }) => fetchQueryAnalytics({ signal }),
    staleTime: 60 * 1000,
  });

  const columns = useMemo(
    () => [
      {
        title: "Account",
        key: "accountCode",
        id: "accountCode",
        width: "140px",
        render: (x) => x.accountCode ?? "-",
        sortFunc: true,
        type: "string",
      },
      {
        title: "Customer",
        key: "customerName",
        id: "customerName",
        width: "180px",
        render: (x) => x.customerName ?? "-",
        sortFunc: true,
        type: "string",
      },
      {
        title: "Invoice",
        key: "invoices",
        id: "invoices",
        width: "160px",
        render: (x) => {
          const invoices = x.invoices ?? [];
          if (invoices.length === 0) return "-";
          return invoices.length === 1 ? invoices[0].invoiceNumber : `${invoices.length} invoices`;
        },
        type: "string",
      },
      {
        title: "Amount",
        key: "amount",
        id: "amount",
        width: "130px",
        render: (x) => formatAmount(x.amount),
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Priority",
        key: "priority",
        id: "priority",
        width: "100px",
        render: (x) => x.priority ?? "-",
        type: "number",
      },
      {
        title: "Reason",
        key: "reason",
        id: "reason",
        width: "200px",
        render: (x) => reasonLabels[x.reason] ?? x.reason,
        type: "enum",
        values: reasonCodes,
        renderFilter: (x) => reasonLabels[x] ?? x,
      },
      {
        title: "Status",
        key: "status",
        id: "status",
        width: "140px",
        render: (x) => (
          <Badge color={queryStatusColors[x.status]}>
            {queryStatusLabels[x.status] ?? x.status}
          </Badge>
        ),
        type: "enum",
        values: ["UnderReview", "Resolved"],
        renderFilter: (x) => queryStatusLabels[x] ?? x,
      },
      {
        title: "Created by",
        key: "createdByName",
        id: "createdByName",
        width: "160px",
        render: (x) => x.createdByName ?? "-",
        type: "string",
      },
      {
        title: "Assigned to",
        key: "assignedToName",
        id: "assignedToName",
        width: "160px",
        render: (x) => x.assignedToName ?? "-",
        type: "string",
      },
      {
        title: "Submitted",
        key: "createdAt",
        id: "createdAt",
        width: "170px",
        render: (x) => formatDateTime(x.createdAt),
        sortFunc: true,
        type: "date",
      },
      {
        title: "Due",
        key: "dueBy",
        id: "dueBy",
        width: "130px",
        render: (x) => {
          if (isTerminalQueryStatus(x.status)) return "Complete";
          if (isQueryOverdue(x)) return <Badge color="red">Overdue</Badge>;
          const days = queryDaysUntilDue(x.dueBy);
          return `${days}d (${queryAgeInDays(x.createdAt)}d open)`;
        },
        sortFunc: true,
        type: "date",
      },
    ],
    [reasonLabels, reasonCodes],
  );

  const filterManager = useTableFilters("queries", columns);
  const { filters } = filterManager;

  const getQueryParams = useCallback(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      filters,
      sort,
      sortDirection,
      searchTerm,
    }),
    [filters, sort, sortDirection, searchTerm, page],
  );

  const query = useTableQuery({
    resource: "queries",
    fetcher: fetchQueries,
    getQueryParams,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["queries", "open-count"] });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters, sort, sortDirection, searchTerm]);

  const handleSort = (key) => {
    if (sort === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDirection("desc");
    }
  };

  const reloadData = () => {
    queryClient.invalidateQueries({ queryKey: ["queries"] });
    queryClient.invalidateQueries({ queryKey: ["new-query", "ledger"] });
  };

  const exportData = async () => {
    const q = buildApiQuery({ filters, sort, sortDirection, searchTerm, export: true });
    const result = await fetchQueries(q);
    return result.data;
  };

  const tableConfig = useMemo(
    () => ({
      key: "queries",
      onClick: (x) => setSelectedQuery(x),
      colCustomisation: true,
      columns,
    }),
    [columns],
  );

  const statistics = [
    { label: "Total", value: query.data ? query.data.rows : null },
    {
      label: "Under review",
      value: query.data ? query.data.statistics.underReview : null,
    },
    {
      label: "Resolved",
      value: query.data ? query.data.statistics.resolved : null,
    },
    {
      label: "Value under query",
      value: analytics ? formatAmount(analytics.totalQueriedValue) : null,
    },
    {
      label: "Avg. resolution",
      value: analytics ? formatResolutionTime(analytics.averageResolutionHours) : null,
    },
  ];

  return (
    <>
      <PageHeader>
        <PageTitle>Queries</PageTitle>
      </PageHeader>
      <ServerTable
        headerAction={
          hasPermission(updateCustomer) ? (
            <Button color="primary" onClick={() => setNewQueryOpen(true)}>
              New query
            </Button>
          ) : null
        }
        config={tableConfig}
        data={query.data?.data || []}
        loading={query.isLoading || query.isPlaceholderData}
        statistics={statistics}
        results={query.data?.rows || 0}
        page={page}
        setPage={setPage}
        pageSize={PAGE_SIZE}
        filterManager={filterManager}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        exportData={exportData}
      />
      <QueryDetails
        query={selectedQuery}
        reasonLabels={reasonLabels}
        onClose={() => setSelectedQuery(null)}
        onChange={reloadData}
      />
      <NewQueryModal isOpen={newQueryOpen} onClose={() => setNewQueryOpen(false)} />
    </>
  );
};

export default Queries;
